import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";
import { categoryMeta, categoryOrder, pageSize, sortOptions } from "@/constants/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import type { ArticleCategory } from "@/types/content";

const timeOptions = [
  { value: "all", label: "全部时间" },
  { value: "30", label: "最近 30 天" },
  { value: "90", label: "最近 90 天" },
];

const heatOptions = [
  { value: "all", label: "全部热度" },
  { value: "hot", label: "热门（1.5 万+）" },
];

export default function Category() {
  usePageMeta(
    "影评与资讯分类 - 色戒未删减版",
    "色戒未删减版分类页，按完整版介绍、影评、拍摄花絮与剧情解析整理资料，支持时间、热度筛选与排序，快速找到想看的深度内容。",
    "色戒完整版,影评分类,影视资讯,剧情解析,拍摄花絮,电影资料",
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const q = searchParams.get("q") ?? "";
  const type = searchParams.get("type") ?? "all";
  const sort = searchParams.get("sort") ?? "date";
  const time = searchParams.get("time") ?? "all";
  const heat = searchParams.get("heat") ?? "all";
  const rawPage = Number(searchParams.get("page") ?? "1");
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    const isDefault = !value || value === "all" || (key === "page" && value === "1");
    if (isDefault) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next);
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
    setFilterOpen(false);
  };

  const filtered = useMemo(() => {
    const now = Date.now();
    const keyword = q.trim();
    const list = articles.filter((article) => {
      const matchKeyword =
        !keyword ||
        article.title.includes(keyword) ||
        article.excerpt.includes(keyword) ||
        article.tags.some((tag) => tag.includes(keyword));
      const matchType = type === "all" || article.category === type;
      const days = (now - new Date(`${article.date}T00:00:00`).getTime()) / 86400000;
      const matchTime = time === "all" || days <= Number(time);
      const matchHeat = heat === "all" || article.views >= 15000;
      return matchKeyword && matchType && matchTime && matchHeat;
    });
    return list.sort((a, b) =>
      sort === "views" ? b.views - a.views : a.date < b.date ? 1 : -1,
    );
  }, [q, type, sort, time, heat]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const hasFilter = Boolean(q) || type !== "all" || time !== "all" || heat !== "all";

  const goPage = (target: number) => {
    updateParam("page", String(target));
    window.scrollTo({ top: 320, behavior: "smooth" });
  };

  const filterPanel = (
    <div className="space-y-8">
      <div>
        <h3 className="font-heading text-sm text-foreground-50">内容类型</h3>
        <div className="mt-4 space-y-1.5">
          <button
            type="button"
            onClick={() => updateParam("type", "all")}
            className={`flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-2 text-left text-[13px] transition-colors ${
              type === "all"
                ? "bg-primary-500/15 text-accent-300"
                : "text-foreground-400 hover:bg-background-900 hover:text-foreground-100"
            }`}
          >
            全部内容
            <span className="font-label text-[11px] text-foreground-600">
              {articles.length}
            </span>
          </button>
          {categoryOrder.map((key: ArticleCategory) => {
            const count = articles.filter((item) => item.category === key).length;
            return (
              <button
                key={key}
                type="button"
                onClick={() => updateParam("type", key)}
                className={`flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-2 text-left text-[13px] transition-colors ${
                  type === key
                    ? "bg-primary-500/15 text-accent-300"
                    : "text-foreground-400 hover:bg-background-900 hover:text-foreground-100"
                }`}
              >
                {categoryMeta[key].label}
                <span className="font-label text-[11px] text-foreground-600">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-sm text-foreground-50">发布时间</h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {timeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => updateParam("time", option.value)}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-[12px] transition-colors ${
                time === option.value
                  ? "border-accent-600/70 bg-accent-500/10 text-accent-300"
                  : "border-background-700 text-foreground-400 hover:border-background-600 hover:text-foreground-100"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-sm text-foreground-50">热度</h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {heatOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => updateParam("heat", option.value)}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-[12px] transition-colors ${
                heat === option.value
                  ? "border-accent-600/70 bg-accent-500/10 text-accent-300"
                  : "border-background-700 text-foreground-400 hover:border-background-600 hover:text-foreground-100"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={clearFilters}
        className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-3 py-2 text-[12px] text-foreground-400 transition-colors hover:border-primary-600/60 hover:text-foreground-100"
      >
        <i className="ri-refresh-line" />
        重置筛选条件
      </button>
    </div>
  );

  return (
    <SiteLayout>
      <section className="border-b border-background-800/70 bg-gradient-to-b from-background-900/60 to-background-950 pb-10 pt-[112px] md:pb-12 md:pt-[136px]">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8">
          <nav className="flex flex-wrap items-center gap-2 text-[12px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-600" />
            <span className="text-foreground-300">电影分类</span>
            {q ? (
              <>
                <i className="ri-arrow-right-s-line text-foreground-600" />
                <span className="text-accent-300">搜索 “{q}”</span>
              </>
            ) : null}
          </nav>
          <div className="mt-6 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl text-foreground-50 md:text-[40px]">
                影评与资讯分类
              </h1>
              <p className="mt-4 text-sm leading-[1.9] text-foreground-400">
                这里收录本站全部《色戒》相关资料：完整版介绍、影评、拍摄花絮与剧情解析。你可以按内容类型浏览，也可以用时间与热度筛选，快速定位想读的段落。
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden text-right md:block">
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-foreground-600">
                  Total Articles
                </p>
                <p className="mt-1 font-heading text-2xl text-accent-300">
                  {filtered.length}
                  <span className="ml-1 text-xs text-foreground-500">篇</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-4 text-sm text-foreground-200 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-950 py-10 md:py-14">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr] lg:gap-10">
            <aside className="hidden lg:block">
              <div className="sticky top-[96px] rounded-lg border border-background-800/80 bg-background-900/40 p-5">
                {filterPanel}
              </div>
            </aside>

            <div>
              <div className="flex flex-col gap-4 border-b border-background-800/80 pb-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2 text-[12px] text-foreground-500">
                  <span>
                    共 <span className="text-foreground-100">{filtered.length}</span> 篇内容
                  </span>
                  {type !== "all" ? (
                    <span className="rounded-full bg-secondary-900/70 px-2.5 py-1 text-secondary-200">
                      {categoryMeta[type as ArticleCategory]?.label ?? type}
                    </span>
                  ) : null}
                  {q ? (
                    <button
                      type="button"
                      onClick={() => updateParam("q", "")}
                      className="flex cursor-pointer items-center gap-1 rounded-full bg-accent-500/10 px-2.5 py-1 text-accent-300"
                    >
                      关键词：{q}
                      <i className="ri-close-line" />
                    </button>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-foreground-500">排序</span>
                  <div className="flex rounded-full border border-background-700 bg-background-900 p-1">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateParam("sort", option.value)}
                        className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] transition-colors ${
                          sort === option.value
                            ? "bg-primary-500 text-foreground-50"
                            : "text-foreground-400 hover:text-foreground-100"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {visible.length ? (
                <>
                  <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                    {visible.map((article) => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>

                  {totalPages > 1 ? (
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                      <button
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() => goPage(currentPage - 1)}
                        className="flex h-9 cursor-pointer items-center gap-1 rounded-md border border-background-700 px-3 text-[12px] text-foreground-300 transition-colors hover:border-accent-600/60 hover:text-accent-300 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <i className="ri-arrow-left-s-line" />
                        上一页
                      </button>
                      {Array.from({ length: totalPages }).map((_, index) => {
                        const pageNumber = index + 1;
                        return (
                          <button
                            key={pageNumber}
                            type="button"
                            onClick={() => goPage(pageNumber)}
                            className={`h-9 w-9 cursor-pointer rounded-md border text-[12px] transition-colors ${
                              pageNumber === currentPage
                                ? "border-primary-500 bg-primary-500 text-foreground-50"
                                : "border-background-700 text-foreground-400 hover:border-background-600 hover:text-foreground-100"
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        disabled={currentPage === totalPages}
                        onClick={() => goPage(currentPage + 1)}
                        className="flex h-9 cursor-pointer items-center gap-1 rounded-md border border-background-700 px-3 text-[12px] text-foreground-300 transition-colors hover:border-accent-600/60 hover:text-accent-300 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        下一页
                        <i className="ri-arrow-right-s-line" />
                      </button>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-700 bg-background-900/30 px-6 py-20 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full border border-background-700 text-2xl text-foreground-600">
                    <i className="ri-inbox-archive-line" />
                  </span>
                  <h2 className="mt-5 font-heading text-lg text-foreground-100">
                    暂无相关内容
                  </h2>
                  <p className="mt-2 max-w-sm text-[13px] leading-relaxed text-foreground-500">
                    当前筛选条件下没有匹配的资料。可以尝试清除筛选，或返回首页看看其他内容。
                  </p>
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm text-foreground-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-refresh-line" />
                      清除筛选
                    </button>
                    <Link
                      to="/"
                      className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-5 text-sm text-foreground-200 transition-colors hover:border-accent-600/60 hover:text-accent-300"
                    >
                      返回首页
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {filterOpen ? (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setFilterOpen(false)}
            className="absolute inset-0 h-full w-full cursor-pointer bg-background-950/70 backdrop-blur-sm"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-xl border-t border-background-700 bg-background-900 px-5 pb-8 pt-5">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-heading text-base text-foreground-50">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setFilterOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-700 text-foreground-300"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            {filterPanel}
          </div>
        </div>
      ) : null}
    </SiteLayout>
  );
}